import { AxiosResponse } from "axios";
import { deleteApi, getApi, postApi } from "./resonanceApi";

/**
 * Investigator "Suivi" dashboard — subject groups (families in case mode, single
 * subjects otherwise). UNCACHED on purpose (unlike questionnaire.service's 10-min
 * caches): the dashboard refetches on every tab entry so reminder stamps and
 * colleague completions show up immediately.
 */

export enum SuiviUrlsEnum {
  GET_SUBJECT_GROUPS = "studies/{studyId}/users/current/subject_groups",
  GET_SUBJECT_GROUP = "studies/{studyId}/users/current/subject_groups/{groupKey}",
  POST_REMINDER_STAMP = "studies/{studyId}/users/current/subject_groups/{groupKey}/reminder_stamps",
  POST_SUBJECT_GROUP = "studies/{studyId}/users/current/subject_groups",
  POST_GROUP_MEMBER = "studies/{studyId}/users/current/subject_groups/{groupKey}/members",
  DELETE_GROUP_MEMBER = "studies/{studyId}/users/current/subject_groups/{groupKey}/members/{memberUserId}",
  POST_MEMBER_EXIT = "studies/{studyId}/users/current/subject_groups/{groupKey}/members/{memberUserId}/exit",
  POST_GROUP_EXIT = "studies/{studyId}/users/current/subject_groups/{groupKey}/exit",
  POST_MEMBER_ACCESS_LINK = "studies/{studyId}/users/current/subject_groups/{groupKey}/members/{memberUserId}/access_link",
  POST_MEMBER_DEATH = "studies/{studyId}/users/current/subject_groups/{groupKey}/members/{memberUserId}/death",
  DELETE_MEMBER_DEATH = "studies/{studyId}/users/current/subject_groups/{groupKey}/members/{memberUserId}/death",
}

/** ref_exit_reasons ids — pinned server-side, see enums/RefExitReason.ts. */
export enum ExitReasonId {
  WITHDRAWAL = 1,
  SCREEN_FAILURE = 2,
  DEATH = 3,
  LOST_TO_FOLLOW_UP = 4,
  ENROLLMENT_ERROR = 5,
  OTHER = 6,
}

export type SubjectGroupingMode = "case" | "subject";
/** Stable codes — the API maps its hyphenated DB statuses before they reach us. */
export type SuiviSequenceStatus = "READY" | "IN_PROGRESS" | "DONE" | "EXPIRED";

export interface SuiviGroupMember {
  user_id: number;
  study_role_id: number;
  role_label: string | null;
  is_authenticative: boolean;
}

export interface SuiviNextAction {
  kind: "FILL" | "REMIND";
  count: number;
  end_date: string;
  subject_role_label: string | null;
}

export interface SuiviGroup {
  group_key: string;
  external_id: string | null;
  members: SuiviGroupMember[];
  to_fill_count: number;
  awaiting_subject_count: number;
  next_action: SuiviNextAction | null;
  worst_end_date: string | null;
  /** Earliest not-yet-open questionnaire window — the "prochaine fenêtre" horizon. */
  next_window: { opens_at: string; label: string | null } | null;
}

export interface SuiviGroupsResponse {
  grouping_mode: SubjectGroupingMode;
  groups: SuiviGroup[];
}

export interface SuiviComponent {
  id: number;
  questionnaire_id: number | null;
  content_id: number | null;
  questionnaire_title: string | null;
  order: number;
  is_done: boolean;
}

export interface SuiviSequenceBase {
  user_sequence_id: number;
  sequence_id: number;
  label: string | null;
  status: SuiviSequenceStatus;
  start_date: string;
  end_date: string;
  subject_user_id: number;
  subject_role_label: string | null;
  progress: { answered: number; total: number };
  avg_time_to_answer: number | null;
  /** Another family member answers this row about the subject (parent's per-child
   *  questionnaire) — label « à remplir par Mère ». Null/absent = subject or me. */
  answerer_user_id?: number | null;
}

export interface SuiviFillSequence extends SuiviSequenceBase {
  filled_by_colleague: boolean;
  components: SuiviComponent[];
}

export interface SuiviHistoryEntry {
  user_sequence_id: number;
  sequence_id: number;
  label: string | null;
  kind: "DONE" | "EXPIRED";
  at: string;
  subject_user_id: number;
  subject_role_label: string | null;
  /** FAMILY = another family member answered about this subject. */
  answered_by: "ME" | "SUBJECT" | "FAMILY";
  answered_by_user_id?: number | null;
}

export interface SuiviMemberDetail {
  user_id: number;
  study_role_id: number;
  role_label: string | null;
  is_authenticative: boolean;
  /** Viewer-independent rank among same-role family members (creation order). */
  sibling_rank: number | null;
  sibling_count: number | null;
  firstname: string | null;
  lastname: string | null;
  phone_number: string | null;
  has_phone: boolean;
  /** Gated like phone: null unless the member's role collects email. */
  email: string | null;
  last_reminder_stamp: { at: string; by_me: boolean } | null;
  /** True while the member is data-empty — gates the remove affordance. */
  deletable: boolean;
  /** Left the study. Data is kept; the member stays listed so the dossier reads whole. */
  exited: boolean;
  exit_reason_code: string | null;
  exit_note: string | null;
  exit_date: string | null;
  /** Died — an outcome, not a withdrawal: the rest of the dossier carries on. */
  deceased: boolean;
  death_date: string | null;
  /** An invitation from this staff role to the member's role is configured. */
  invitable: boolean;
  /** Last access link sent to this member by ANY colleague. */
  invited_at: string | null;
  /** Has been through signup — the link has done its job. */
  registered: boolean;
}

export interface SuiviNextWindow {
  sequence_id: number;
  label: string | null;
  opens_at: string;
  /** FILL = the investigator will fill it; SUBJECT = the member's own. */
  kind: "FILL" | "SUBJECT";
  subject_user_id: number;
  subject_role_label: string | null;
}

/**
 * One dossier-level reference-date rule and where it stands for this dossier
 * (case mode only). Field names hand-mirror the API's CaseReferenceDateStatusRow.
 * RESOLVED = the date-less members hold a derived date; WAITING = the rule waits
 * on source members' own dates; NO_SOURCE = no source date will ever exist (e.g.
 * death before discharge); UNCONFIGURED = a migrated rule the research team has
 * not completed yet.
 */
export interface SuiviReferenceDateStatus {
  definition_id: number;
  label: string;
  case_policy: "NONE" | "EARLIEST" | "LATEST" | "FIRST" | "LAST";
  case_wait_for_all: boolean;
  source_role_id: number | null;
  source_role_label: string | null;
  status: "RESOLVED" | "WAITING" | "NO_SOURCE" | "UNCONFIGURED";
  /** 'YYYY-MM-DD' — RESOLVED only. */
  derived_value: string | null;
  /** RESOLVED only, and only when that member is within the viewer's site. */
  derived_from_user_id: number | null;
  /** Gate = eligible, living source members; resolved = those with their own date. */
  expected_source_count: number;
  resolved_source_count: number;
  deceased_source_count: number;
  /** Gate members without their own date, split by the viewer's site visibility. */
  missing_visible_user_ids: number[];
  missing_hidden_count: number;
}

export interface SuiviGroupDetail {
  grouping_mode: SubjectGroupingMode;
  group_key: string;
  external_id: string | null;
  members: SuiviMemberDetail[];
  to_fill: SuiviFillSequence[];
  awaiting_subjects: SuiviSequenceBase[];
  history: SuiviHistoryEntry[];
  /** One FILL entry (the investigator's next work here) + each member's own next window. */
  next_windows: SuiviNextWindow[];
  /** Case mode only. Optional so an older API renders the page unchanged. */
  reference_dates?: SuiviReferenceDateStatus[];
}

export const getSuiviGroups = ({
  studyId,
  lang,
}: {
  studyId: number;
  lang: string;
}): Promise<AxiosResponse<SuiviGroupsResponse>> =>
  getApi<SuiviGroupsResponse>(SuiviUrlsEnum.GET_SUBJECT_GROUPS, { studyId }, { lang });

export const getSuiviGroupDetail = ({
  studyId,
  groupKey,
  lang,
}: {
  studyId: number;
  groupKey: string;
  lang: string;
}): Promise<AxiosResponse<SuiviGroupDetail>> =>
  // group_key is free text in case mode (external_id) — encode it so the path
  // segment survives spaces/slashes; the API compares the decoded value.
  getApi<SuiviGroupDetail>(
    SuiviUrlsEnum.GET_SUBJECT_GROUP,
    { studyId, groupKey: encodeURIComponent(groupKey) },
    { lang }
  );

export const postSuiviReminderStamp = ({
  studyId,
  groupKey,
  subjectUserId,
}: {
  studyId: number;
  groupKey: string;
  subjectUserId: number;
}): Promise<AxiosResponse<void>> =>
  postApi<void>(
    SuiviUrlsEnum.POST_REMINDER_STAMP,
    { studyId, groupKey: encodeURIComponent(groupKey) },
    { subject_user_id: subjectUserId }
  );

/** « Ajouter un dossier »: numéro (allowlist-validated server-side) + initial members. */
export const postCreateSuiviGroup = (
  { studyId }: { studyId: number },
  body: { external_id: string; members: { study_role_id: number; count: number }[] }
): Promise<AxiosResponse<{ group_key: string }>> =>
  postApi<{ group_key: string }>(SuiviUrlsEnum.POST_SUBJECT_GROUP, { studyId }, body);

export const postAddSuiviGroupMember = ({
  studyId,
  groupKey,
  studyRoleId,
}: {
  studyId: number;
  groupKey: string;
  studyRoleId: number;
}): Promise<AxiosResponse<void>> =>
  postApi<void>(
    SuiviUrlsEnum.POST_GROUP_MEMBER,
    { studyId, groupKey: encodeURIComponent(groupKey) },
    { study_role_id: studyRoleId }
  );

export const deleteSuiviGroupMember = ({
  studyId,
  groupKey,
  memberUserId,
}: {
  studyId: number;
  groupKey: string;
  memberUserId: number;
}): Promise<AxiosResponse<void>> =>
  deleteApi<void>(SuiviUrlsEnum.DELETE_GROUP_MEMBER, {
    studyId,
    groupKey: encodeURIComponent(groupKey),
    memberUserId,
  });

export interface SuiviExitBody {
  ref_exit_reason_id: number;
  exit_note?: string | null;
  /** false permanently removes the participant from the research data — not a display
   *  flag. The UI must ask explicitly. */
  keep_data: boolean;
}

/** Take ONE participant out of the study, keeping their data. */
export const postSuiviMemberExit = (
  { studyId, groupKey, memberUserId }: { studyId: number; groupKey: string; memberUserId: number },
  body: SuiviExitBody
): Promise<AxiosResponse<void>> =>
  postApi<void>(
    SuiviUrlsEnum.POST_MEMBER_EXIT,
    { studyId, groupKey: encodeURIComponent(groupKey), memberUserId },
    body
  );

/** Withdraw the whole dossier — every member, including any this investigator cannot see. */
export const postSuiviGroupExit = (
  { studyId, groupKey }: { studyId: number; groupKey: string },
  body: SuiviExitBody
): Promise<AxiosResponse<void>> =>
  postApi<void>(
    SuiviUrlsEnum.POST_GROUP_EXIT,
    { studyId, groupKey: encodeURIComponent(groupKey) },
    body
  );

/** Send (or re-send) a dossier member the link that lets them register and claim their
 *  own row. email/phone are only needed when the member's fiche has no contact yet. */
export const postSuiviMemberAccessLink = (
  { studyId, groupKey, memberUserId }: { studyId: number; groupKey: string; memberUserId: number },
  body: { channel: "EMAIL" | "SMS"; email?: string | null; phone_number?: string | null; lang: string }
): Promise<AxiosResponse<void>> =>
  postApi<void>(
    SuiviUrlsEnum.POST_MEMBER_ACCESS_LINK,
    { studyId, groupKey: encodeURIComponent(groupKey), memberUserId },
    body
  );

export const postSuiviMemberDeath = (
  { studyId, groupKey, memberUserId }: { studyId: number; groupKey: string; memberUserId: number },
  body: { death_date?: string | null; note?: string | null }
): Promise<AxiosResponse<void>> =>
  postApi<void>(
    SuiviUrlsEnum.POST_MEMBER_DEATH,
    { studyId, groupKey: encodeURIComponent(groupKey), memberUserId },
    body
  );

export const deleteSuiviMemberDeath = ({
  studyId,
  groupKey,
  memberUserId,
}: {
  studyId: number;
  groupKey: string;
  memberUserId: number;
}): Promise<AxiosResponse<void>> =>
  deleteApi<void>(SuiviUrlsEnum.DELETE_MEMBER_DEATH, {
    studyId,
    groupKey: encodeURIComponent(groupKey),
    memberUserId,
  });
